import type { HTMLAttributes } from "react";
import { cn } from "#/classname.ts";

const pads = {
  default: "",
  xs: "p-1",
  sm: "p-2",
  md: "p-4",
  lg: "p-6",
  xl: "p-8",
  "2xl": "p-10",
  "3xl": "p-12",
} as const;

const bgs = {
  default: "",
  canvas: "bg-white",
  surface: "bg-secondary-25",
  "surface-2": "bg-secondary-50",
  "surface-3": "bg-secondary-100",
  invert: "bg-secondary-900 text-white",
  success: "bg-success-100",
  warning: "bg-warning-400",
  danger: "bg-danger-400",
  accent: "bg-accent-200",
};

const gaps = {
  none: "",
  xs: "gap-1",
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-6",
  xl: "gap-8",
  "2xl": "gap-10",
  "3xl": "gap-12",
  "4xl": "gap-14",
} as const;

const aligns = {
  default: "",
  center: "items-center",
  start: "items-start",
  end: "items-end",
  baseline: "items-baseline",
  stretch: "items-stretch",
} as const;

const justifies = {
  default: "",
  center: "justify-center",
  start: "justify-start",
  end: "justify-end",
  baseline: "justify-baseline",
  between: "justify-between",
  around: "justify-around",
  evenly: "justify-evenly",
} as const;

const radii = {
  default: "",
  xs: "rounded-xs",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  "3xl": "rounded-3xl",
  "4xl": "rounded-4xl",
};

const dividers = {
  default: "",
  dim: "rule-1 rule-secondary-100",
  strong: "rule-1 rule-secondary-700",
} as const;

// Layout components can only render generic containers (i.e. not <button>)
type Container =
  | "div"
  | "section"
  | "article"
  | "aside"
  | "nav"
  | "header"
  | "search"
  | `h${1 | 2 | 3 | 4 | 5 | 6}`
  | "dl"
  | "ul"
  | "ol"
  // @todo: Figure out how to support tags with more attributes than HTMLElement
  // | "fieldset"
  | "li";

type Props = HTMLAttributes<HTMLElement> & {
  /** Change which element is rendered */
  as?: Container;
  /** Set padding on all sides of the container */
  p?: keyof typeof pads;
  /** Set background-color from surface tokens */
  bg?: keyof typeof bgs;
  /** Set space between children. Defaults to `md` */
  gap?: keyof typeof gaps;
  /** Control cross-axis alignment */
  align?: keyof typeof aligns;
  /** Control main-axis alignment */
  justify?: keyof typeof justifies;
  /** Set border-radius */
  rounded?: keyof typeof radii;
  /** Draw a border between children */
  divider?: keyof typeof dividers;
};

function defaults<T extends Props>({
  as: Tag = "div",
  p = "default",
  bg = "default",
  gap = "md",
  align = "default",
  justify = "default",
  rounded = "default",
  divider = "default",
  className,
  ...rest
}: T) {
  return {
    Tag,
    className: cn(
      pads[p],
      bgs[bg],
      gaps[gap],
      aligns[align],
      justifies[justify],
      radii[rounded],
      dividers[divider],
      className,
    ),
    ...rest,
  };
}

/**
 * Stacks its children vertically.
 * @example
 * <Box as="ul" p="xl" gap="lg">
 *  <li>A</li>
 *  <li>B</li>
 * </Box>
 */
export function Box(props: Props) {
  const { Tag, className, ...rest } = defaults(props);
  return <Tag {...rest} className={cn("flex flex-col", className)} />;
}

type RowProps = Props & {
  /** Toggle wrapping. Defaults to `true` to avoid overflow breakage */
  wrap?: boolean;
};

/**
 * Stacks its children horizontally.
 * @example
 * <Row gap="sm">
 *   <Button>A</Button>
 *   <Button>B</Button>
 *   <Button>C</Button>
 * </Row>
 */
export function Row(props: RowProps) {
  const { Tag, wrap = true, className, ...rest } = defaults(props);
  return (
    <Tag {...rest} className={cn("flex", className, wrap && "flex-wrap")} />
  );
}

/**
 * A Box with default padding, border-radius and background.
 * Supports the exact same props as Box for customisation.
 * @example
 * <Card p="sm" bg="surface">...</Card>
 */
export function Card(props: Props) {
  const { p = "lg", rounded = "3xl", bg = "canvas", ...rest } = props;
  return <Box p={p} rounded={rounded} bg={bg} {...rest} />;
}

export function Center(props: Props) {
  const { Tag, className, ...rest } = defaults(props);
  return (
    <Box
      {...rest}
      align="center"
      justify="center"
      className={cn("text-center", className)}
    />
  );
}

const thresholds = {
  sm: "[--above:var(--container-sm)]",
  md: "[--above:var(--container-md)]",
  lg: "[--above:var(--container-lg)]",
  xl: "[--above:var(--container-xl)]",
  "2xl": "[--above:var(--container-2xl)]",
  "3xl": "[--above:var(--container-3xl)]",
  "4xl": "[--above:var(--container-4xl)]",
  "5xl": "[--above:var(--container-5xl)]",
} as const;

type ColumnsProps = Props & {
  /** Container width where layout switches from vertical to horizontal */
  above?: keyof typeof thresholds;
};

/**
 * Switches from a vertical layout to equal-width columns above a specified container width.
 * @example
 * <Columns threshold="xl">
 *   <Box>...</Box>
 *   <img src="..." />
 * </Columns>
 */
export function Columns(props: ColumnsProps) {
  const { Tag, above = "md", className, ...rest } = defaults(props);
  return (
    <Tag
      {...rest}
      className={cn(
        "flex flex-wrap",
        // Ensure children can shrink and grow
        "*:min-w-0 *:grow ",
        // if container width is above --above, child basis resolves to massive value, capped to 100%
        // all children being 100% width means they stack
        // https://heydonworks.com/article/the-flexbox-holy-albatross/
        "*:basis-[calc((var(--above)-100%)*999)]",
        thresholds[above],
        className,
      )}
    />
  );
}

const sidebarSizes = {
  xs: "*:first:basis-xs",
  sm: "*:first:basis-sm",
  md: "*:first:basis-md",
  lg: "*:first:basis-lg",
} as const;

const contentSizes = {
  xs: "*:last:min-w-xs",
  sm: "*:last:min-w-sm",
  md: "*:last:min-w-md",
  lg: "*:last:min-w-lg",
} as const;

type SidebarProps = RowProps & {
  size?: keyof typeof sidebarSizes;
  contentMin?: keyof typeof contentSizes;
};

export function Sidebar(props: SidebarProps) {
  const {
    className,
    size = "xs",
    contentMin = "sm",
    ...rest
  } = defaults(props);
  return (
    <Row
      {...rest}
      className={cn(
        className,
        "content-start",
        // First child is sidebar, fixed width. e.g. flex: 1 1 24rem
        "*:shrink *:first:grow",
        sidebarSizes[size],
        // Last child is main, takes up all space. e.g. flex: 999 1 0
        "*:last:grow-999 *:last:basis-0",
        contentSizes[contentMin],
      )}
    />
  );
}

/**
 * Renders a Row with a mandatory bg colour,
 * configured for displaying an icon + alert message
 * @example
 * <Alert bg="warning">
 *  <AlertCircle />
 *  This will do a thing.
 * </Alert>
 */
export function Alert(props: RowProps & Required<Pick<RowProps, "bg">>) {
  const { gap = "sm", rounded = "xl", p = "md", align = "start" } = props;
  const { wrap = false, ...rest } = defaults(props);
  return (
    <Row
      {...rest}
      gap={gap}
      rounded={rounded}
      p={p}
      align={align}
      wrap={wrap}
    />
  );
}

export function Spacer() {
  return <div className="flex-1" />;
}
